// -----------------------------------------------------------------------------
// WhatsAppPanel  (Wave B)
//
// Action panel body for WHATSAPP_SENT.
// Reuses IosWhatsAppComposerDialog (same pattern as EmailActionBody).
// Opens the composer dialog immediately; on send success calls onComplete.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import Iconify from 'src/components/Iconify';
import IosWhatsAppComposerDialog from 'src/sections/tasks/composers/IosWhatsAppComposerDialog';

export default function WhatsAppPanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onComplete,
    onClose,
}) {
    const [composerOpen, setComposerOpen] = useState(true);

    const phoneNumber = leadDetail?.lead_contact?.phone
        || leadDetail?.phone
        || leadDetail?.originalData?.lead_contact?.phone
        || '';

    const toName = [leadDetail?.first_name, leadDetail?.last_name].filter(Boolean).join(' ') || 'Patient';

    const handleSendSuccess = () => {
        setComposerOpen(false);
        onComplete?.(item);
    };

    const handleComposerClose = () => {
        setComposerOpen(false);
        onClose?.();
    };

    return (
        <Box
            data-testid="whatsapp-panel"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                px: 3,
                py: 2.5,
                gap: 1.75,
                textAlign: 'center',
            }}
        >
            <Box
                sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    bgcolor: 'rgba(37, 211, 102, 0.10)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Iconify icon="mdi:whatsapp" width={24} height={24} sx={{ color: '#25D366' }} />
            </Box>
            <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    {item?.title || 'Send WhatsApp'}
                </Typography>
                {phoneNumber ? (
                    <Typography sx={{ fontSize: '13px', color: '#6e6e73', mt: 0.5 }}>
                        To: {toName} · {phoneNumber}
                    </Typography>
                ) : (
                    <Typography sx={{ fontSize: '13px', color: '#FF5630', mt: 0.5 }}>
                        No phone number on file
                    </Typography>
                )}
            </Box>

            <Button
                data-testid="whatsapp-open-composer"
                variant="contained"
                disableElevation
                onClick={() => setComposerOpen(true)}
                startIcon={<Iconify icon="mdi:whatsapp" width={16} height={16} />}
                sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    bgcolor: '#25D366',
                    '&:hover': { bgcolor: '#1da851' },
                    boxShadow: 'none',
                }}
            >
                Open WhatsApp
            </Button>

            <IosWhatsAppComposerDialog
                open={composerOpen}
                onClose={handleComposerClose}
                practiceId={practiceId}
                leadId={leadId}
                phoneNumber={phoneNumber}
                leadDetail={leadDetail}
                onSendSuccess={handleSendSuccess}
            />
        </Box>
    );
}

WhatsAppPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

WhatsAppPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
};
